import type { JsonValue } from '@shared/api/apiClient';

export const DataConnectionType = {
    Csv: 1,
    Excel: 2,
    MsAccess: 3,
    MsSqlServer: 4,
    PostgreSql: 5,
} as const;

export type DataConnectionType = typeof DataConnectionType[keyof typeof DataConnectionType];

export type DataTypeValue = 'text' | 'number' | 'date' | 'boolean' | 'url';

export interface ConnectionTypeModel {
    Type: DataConnectionType;
    Name?: string;
    DisplayName?: string;
    IsLinux?: boolean;
}

export interface SourceConfig {
    type: string;
    schemaname?: string;
    name: string;
    identifier: string;
    columns: SelectedSourceColumn[];
}

export interface DataSourceModel {
    ID: string;
    VersionToken: string;
    FacilityID: string;
    DataConnectionID: string;
    Name: string;
    Type: DataConnectionType;
    TypeName: string;
    ConnectionConfig: Record<string, unknown>;
    SourceConfig: SourceConfig;
    LastTestSuccessful?: boolean | null;
    LastTestDate?: string | null;
    LastTestMessage?: string | null;
    FdwTableName?: string | null;
    FdwStatus?: number | null;
    FdwLastSyncedDate?: string | null;
    FdwLastSyncError?: string | null;
}

export interface DataSourceSaveModel {
    ID?: string;
    VersionToken?: string;
    FacilityID: string;
    DataConnectionID?: string | null;
    Name: string;
    Type?: DataConnectionType;
    ConnectionConfig?: Record<string, unknown>;
    SourceConfig: SourceConfig;
    ConnectionTestSuccessful?: boolean;
    ConnectionTestMessage?: string | null;
}

export interface AssetIdentityMappingDefinition {
    SourceColumnName: string;
    DataType: number;
    AssetMatchTargetType: number;
    AssetMatchAttributeDefinitionID?: string | null;
}

export interface AssetIdentityMappingCountRequest {
    FacilityID: string;
    DataSourceID: string;
    AssetClassID: string;
    Mappings: AssetIdentityMappingDefinition[];
}

export interface AssetIdentityMappingCountResult {
    Count: number;
}

export interface ConnectionValidationRequest {
    FacilityID: string;
    Type?: DataConnectionType;
    ConnectionConfig?: Record<string, unknown>;
    DataConnectionID?: string | null;
}

export interface ConnectionValidationResult {
    Successful?: boolean;
    Message?: string | null;
}

export interface DataSourceFileModel {
    Name: string;
    Path: string;
    SizeBytes: number;
    LastModified: string;
    Extension: string;
    Referenced: boolean;
}

export type DataSourceFileUploadConflictBehavior = 'Reject' | 'Overwrite' | 'AutoRename';

export interface DataSourceFileUploadResult {
    File: DataSourceFileModel;
}

export interface SourceObject {
    Type?: string;
    SchemaName?: string | null;
    Name: string;
    Identifier: string;
}

export interface SourceColumn {
    Name: string;
    Identifier: string;
    DataType: string;
    SourceDataType?: DataTypeValue;
}

export interface SelectedSourceColumn {
    name: string;
    identifier: string;
    datatype: DataTypeValue;
    sourcedatatype?: DataTypeValue;
}

export interface DiscoverSourcesRequest {
    FacilityID: string;
    Type: DataConnectionType;
    ConnectionConfig: Record<string, unknown>;
    DataConnectionID?: string | null;
}

export interface DiscoverColumnsRequest extends DiscoverSourcesRequest {
    Source: SourceObject;
}

export type DataSourceJsonBody = JsonValue;
